import { useCallback } from "react";
import { useSelector } from "react-redux";
import {
  type PackageAppLoadedRawEvent,
  type PackagesRawEvent,
  type PackageEntry,
  type PackageHTSEvent,
  type PackageReviewDetails,
  type PackageReviewDetailsLoadedRawEvent,
  PackagesRawEventName,
  PackageCommonTrackingEventsName,
  PACKAGE_HTS_EVENT_TRANSFORMER_HOOKS,
  PACKAGE_PRODUCT_TYPE,
  usePackageReviewDetailsLoadedTransformer,
} from "@b2bportal/core-packages/tracking";
import type { UseInstantTransformEvent } from "@b2bportal/core-tracking";
import type { EventTransformerHooks } from "@b2bportal/core-tracking";
import { Funnel } from "@b2bportal/core-types";
import { useTrackFunnelEntry } from "./hooks";
import { usePackageUpSellAttribution } from "./upSell";
import { usePackageEventAttribution } from "./upSell";
import { getSearchFormPackageType } from "@/store/packages/selectors/packageSearchSelectors";

/**
 * RBC-specific transformer for package_entry event.
 * Fires on every funnel transition (not just once per session).
 */
export const useRbcPackageAppLoadedTransformer: UseInstantTransformEvent<
  PackageAppLoadedRawEvent,
  PackageEntry
> = () => {
  const shouldTrack = useTrackFunnelEntry(Funnel.Package);
  const packageType = useSelector(getSearchFormPackageType);
  const upSell = usePackageUpSellAttribution();

  return useCallback(
    (event) => {
      if (!shouldTrack) {
        return undefined;
      }

      const name: PackageCommonTrackingEventsName.package_entry =
        PackageCommonTrackingEventsName.package_entry;

      return {
        name,
        properties: {
          entry: event.properties.entry,
          productType: PACKAGE_PRODUCT_TYPE,
          package_type: packageType,
          ...upSell,
        },
      };
    },
    [packageType, shouldTrack, upSell]
  );
};

/**
 * Adds the up-sell attribution to package_review_details, which the core
 * transformer builds from redux.
 */
export const useRbcPackageReviewDetailsTransformer: UseInstantTransformEvent<
  PackageReviewDetailsLoadedRawEvent,
  PackageReviewDetails
> = (name) => {
  const transform = usePackageReviewDetailsLoadedTransformer(name);
  const attribution = usePackageEventAttribution();

  return useCallback(
    (event) => {
      const result = transform(event);
      if (result == null) return result;

      return {
        ...result,
        properties: { ...result.properties, ...attribution },
      };
    },
    [transform, attribution]
  );
};

/**
 * RBC-specific package event transformer hooks (overrides only)
 */
export const RBC_PACKAGES_HTS_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  PackagesRawEvent,
  PackageHTSEvent
> = {
  [PackagesRawEventName.PackageAppLoaded]: [useRbcPackageAppLoadedTransformer],
  [PackagesRawEventName.PackageReviewDetailsLoaded]: [
    useRbcPackageReviewDetailsTransformer,
  ],
};

/**
 * All RBC package transformers - core transformers with RBC overrides
 * RBC transformers REPLACE core transformers for the same event (not merge)
 */
export const ALL_RBC_PACKAGE_TRANSFORMERS: EventTransformerHooks<
  PackagesRawEvent,
  PackageHTSEvent
> = {
  // Start with all core transformers
  ...PACKAGE_HTS_EVENT_TRANSFORMER_HOOKS,
  // Override with RBC-specific transformers (replaces, not merges)
  ...RBC_PACKAGES_HTS_EVENT_TRANSFORMER_HOOKS,
};
